import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import "./LoginPage.css";
import api from "services/api";
import LoginPage from "./LoginPage";
import { parseLocalStorage } from "./utils";
import { useAuth } from "./useAuth";

const SignUpPage = () => {
  const navigate = useNavigate();
  const { handleStripeCheckout, loginWithGoogle } = useAuth();
  const [showLogin, setShowLogin] = useState(false);
  const [isEmailSignup, setIsEmailSignup] = useState(false);
  const [signupStep, setSignupStep] = useState(1);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    password: "",
    confirmPassword: "",
  });
  const [errorMessage, setErrorMessage] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const validateEmail = (email) =>
    /^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,6}$/.test(email);

  const handleContinue = (e) => {
    e.preventDefault();
    const { email } = formData;

    if (!email) {
      setErrorMessage("Email is required.");
      return;
    }

    if (!validateEmail(email)) {
      setErrorMessage("Invalid email format.");
      return;
    }

    setErrorMessage("");
    setIsEmailSignup(true);
    setSignupStep(2);
  };

  const handleGoogleSignup = async () => {
    setIsLoading(true);
    setErrorMessage("");
    try {
      const storedVariables = parseLocalStorage();
      await loginWithGoogle(storedVariables);
    } catch (error) {
      setErrorMessage("Failed to sign up with Google. Please try again.");
      console.error("Error during Google signup:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleEmailSignup = async (e) => {
    e.preventDefault();
    const { email, password, confirmPassword } = formData;

    if (!password || !confirmPassword) {
      setErrorMessage("All fields are required.");
      return;
    }

    if (password !== confirmPassword) {
      setErrorMessage("Passwords do not match.");
      return;
    }

    try {
      const storedVariables = parseLocalStorage();
      const payload = {
        ...formData,
        storedVariables,
      };

      const response = await api.post("/email-signup/", payload, {
        validateStatus: (status) => status >= 200 && status < 500,
      });

      if (response.status !== 200) {
        if (response.data && response.data.error) {
          throw new Error(response.data.error);
        }
        throw new Error("Signup failed. Please try again.");
      }

      setFormData({ name: "", email: "", password: "", confirmPassword: "" });
      setErrorMessage("");

      const { accessToken, selectedPackageId } = response.data;

      if (!accessToken) {
        throw new Error("Signup successful but no access token received.");
      }

      localStorage.setItem("accessToken", accessToken);

      if (selectedPackageId) {
        handleStripeCheckout(accessToken, selectedPackageId);
      }

      navigate("/subscriptions");
    } catch (error) {
      console.error("Error during signup:", error);
      setErrorMessage(error.message);
    }
  };

  if (showLogin) {
    return <LoginPage />;
  }

  return (
    <div className="min-h-screen">
      <div className="max-w-md">
        <h1 className="text-[#1563FF] uppercase">HEALTHITRACK</h1>

        <h1 className="text-3xl">
          {isEmailSignup ? "Create your password" : "Sign up for HealthiTrack"}
        </h1>
        <p className="text-gray-600">
          {isEmailSignup
            ? "Choose a secure password to protect your account"
            : "Create an account to discover your treatment options"}
        </p>

        {isLoading && <div className="loading">Loading...</div>}

        {errorMessage && <div className="error-message">{errorMessage}</div>}

        {!isEmailSignup ? (
          <>
            <button
              onClick={handleGoogleSignup}
              disabled={isLoading}
              className="social-button"
            >
              <img src="/google.png" alt="Google logo" />
              Sign up with Google
            </button>

            <div className="divider">
              <span>or</span>
            </div>

            <input
              type="email"
              name="email"
              value={formData.email}
              onChange={handleInputChange}
              placeholder="Email"
              className="input"
            />

            <button
              onClick={handleContinue}
              disabled={isLoading || !formData.email}
              className="primary-button"
            >
              Continue with Email
            </button>

            <div className="text-center">
              <p>
                Already have an account?{" "}
                <button
                  onClick={() => setShowLogin(true)}
                  className="link-button"
                >
                  Log in
                </button>
              </p>
            </div>

            <div className="privacy-notice">
              <svg viewBox="0 0 24 24" fill="currentColor">
                <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
              </svg>
              <span>
                HealthiTrack takes your privacy seriously with industry leading
                encryption.
              </span>
            </div>
          </>
        ) : (
          <>
            <input
              type="email"
              value={formData.email}
              disabled
              className="input"
            />

            <div className="password-input-container">
              <input
                type={showPassword ? "text" : "password"}
                name="password"
                value={formData.password}
                onChange={handleInputChange}
                placeholder="Create a password"
                className="input mt-2"
              />
              <button
                type="button"
                className={`password-toggle ${showPassword ? "active" : ""}`}
                onClick={() => setShowPassword(!showPassword)}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" />
                  <line x1="1" y1="1" x2="23" y2="23" />
                </svg>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                  <circle cx="12" cy="12" r="3" />
                </svg>
              </button>
            </div>

            <div className="password-input-container">
              <input
                type={showConfirmPassword ? "text" : "password"}
                name="confirmPassword"
                value={formData.confirmPassword}
                onChange={handleInputChange}
                placeholder="Confirm your password"
                className="input mt-2"
              />
              <button
                type="button"
                className={`password-toggle ${
                  showConfirmPassword ? "active" : ""
                }`}
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" />
                  <line x1="1" y1="1" x2="23" y2="23" />
                </svg>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                  <circle cx="12" cy="12" r="3" />
                </svg>
              </button>
            </div>

            <button
              onClick={handleEmailSignup}
              disabled={
                isLoading || !formData.password || !formData.confirmPassword
              }
              className="primary-button"
            >
              Create account
            </button>

            <button
              onClick={() => setIsEmailSignup(false)}
              className="back-button"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M19 12H5M12 19l-7-7 7-7" />
              </svg>
              Back
            </button>
          </>
        )}
      </div>
    </div>
  );
};

export default SignUpPage;
